import * as React from "react";
import { type VariantProps, cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-medium transition-all duration-ev focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ev-focus disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-ev-blue text-white hover:bg-ev-hero-blue rounded-full",
        secondary:
          "border-2 border-ev-disabled bg-transparent text-ev-text-primary hover:bg-ev-outline-hover rounded-full",
        destructive:
          "bg-ev-danger text-white hover:bg-ev-danger-hover rounded-full",
        outline:
          "border-2 border-ev-disabled bg-transparent text-ev-text-primary hover:bg-ev-outline-hover rounded-full",
        ghost:
          "bg-transparent text-ev-text-primary hover:bg-ev-surface-subtle rounded-2xl",
        link: "bg-transparent text-ev-text-primary underline-offset-4 hover:underline rounded-lg",
        gradient:
          "bg-gradient-to-r from-ev-hero-blue to-ev-green text-white font-semibold hover:brightness-110 rounded-full",
      },
      size: {
        sm: "text-sm px-3 py-1.5",
        md: "px-4 py-2",
        lg: "text-lg px-6 py-3",
        icon: "h-10 w-10 p-0 rounded-lg bg-ev-hero-blue text-white hover:bg-ev-hero-blue",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  rounded?: "default" | "pill";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      loading = false,
      disabled = false,
      children,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        type="button"
        ref={ref}
        disabled={isDisabled}
        className={cn(
          buttonVariants({ variant, size }),
          rounded === "pill" && "rounded-full",
          loading &&
            "bg-ev-disabled text-white pointer-events-none opacity-70 rounded-full",
          disabled &&
            !loading &&
            "bg-ev-disabled text-white opacity-50 cursor-not-allowed pointer-events-none rounded-full",
          className,
        )}
        {...props}
      >
        {loading ? <Spinner /> : children}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
